<template>
  <div class="consulta-compra-container">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Filtros Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-filter me-2"></i>Compra</h5>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- Tipo de Período -->
              <div class="col-md-3">
                <label class="form-label">Tipo de Período</label>
                <select v-model="filtros.tipoPeriodo" class="form-select" @change="onTipoPeriodoChange">
                  <option value="dia">Día</option>
                  <option value="semana">Semana</option>
                  <option value="mes">Mes</option>
                  <option value="anio">Año</option>
                  <option value="rango">Rango Personalizado</option>
                </select>
              </div>

              <!-- Fecha Específica (para día) -->
              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'dia'">
                <label class="form-label">Fecha</label>
                <input type="date" v-model="filtros.fechaEspecifica" class="form-control">
              </div>

              <!-- Semana -->
              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'semana'">
                <label class="form-label">Semana</label>
                <input type="week" v-model="filtros.semana" class="form-control">
              </div>

              <!-- Mes y Año -->
              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'mes'">
                <label class="form-label">Mes</label>
                <select v-model="filtros.mes" class="form-select">
                  <option value="1">Enero</option>
                  <option value="2">Febrero</option>
                  <option value="3">Marzo</option>
                  <option value="4">Abril</option>
                  <option value="5">Mayo</option>
                  <option value="6">Junio</option>
                  <option value="7">Julio</option>
                  <option value="8">Agosto</option>
                  <option value="9">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
              </div>

              <div class="col-md-2" v-if="filtros.tipoPeriodo === 'mes'">
                <label class="form-label">Año</label>
                <input type="number" v-model="filtros.anioMes" class="form-control" min="2020" :max="currentYear">
              </div>

              <!-- Año -->
              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'anio'">
                <label class="form-label">Año</label>
                <input type="number" v-model="filtros.anio" class="form-control" min="2020" :max="currentYear">
              </div>

              <!-- Rango Personalizado -->
              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'rango'">
                <label class="form-label">Fecha Inicio</label>
                <input type="date" v-model="filtros.fechaInicio" class="form-control">
              </div>

              <div class="col-md-3" v-if="filtros.tipoPeriodo === 'rango'">
                <label class="form-label">Fecha Fin</label>
                <input type="date" v-model="filtros.fechaFin" class="form-control">
              </div>

              <!-- Botón Buscar -->
              <div class="col-12">
                <button class="btn btn-primary me-2" @click="buscarCompras">
                  <i class="fas fa-search me-2"></i>
                  Buscar
                </button>
                <button class="btn btn-secondary" @click="limpiarFiltros">
                  <i class="fas fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Ingresos</h6>
                <h3 class="mb-0">{{ estadisticas.totalIngresos }}</h3>
              </div>
              <div class="stat-icon bg-primary">
                <i class="fas fa-shopping-cart"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Monto Invertido</h6>
                <h3 class="mb-0">{{ formatoMoneda(estadisticas.montoInvertido) }}</h3>
              </div>
              <div class="stat-icon bg-success">
                <i class="fas fa-dollar-sign"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Cantidad Productos</h6>
                <h3 class="mb-0">{{ estadisticas.cantidadProductos }}</h3>
              </div>
              <div class="stat-icon bg-warning">
                <i class="fas fa-box"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Compras -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-list me-2"></i>Listado de Compras</h5>
              </div>
              <div class="col-auto">
                <div class="d-flex align-items-center">
                  <label class="me-2 mb-0">Mostrar:</label>
                  <select v-model="paginacion.porPagina" class="form-select form-select-sm" style="width: auto;" @change="cambiarPorPagina">
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover table-striped mb-0">
                <thead class="table-light">
                  <tr>
                    <th>N° Comprobante</th>
                    <th>Serie</th>
                    <th>Fecha</th>
                    <th>Proveedor</th>
                    <th>Productos</th>
                    <th class="text-end">Total</th>
                    <th class="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="compras.length === 0">
                    <td colspan="7" class="text-center py-4">
                      <i class="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                      <p class="text-muted mb-0">No se encontraron compras con los filtros seleccionados</p>
                    </td>
                  </tr>
                  <tr v-for="compra in comprasPaginadas" :key="compra.id">
                    <td><span class="badge bg-primary">{{ compra.ncomprobante }}</span></td>
                    <td><span class="badge bg-secondary">{{ compra.serie }}</span></td>
                    <td>
                      <div>{{ formatearFecha(compra.fecha) }}</div>
                      <small class="text-muted">{{ formatearHora(compra.fecha) }}</small>
                    </td>
                    <td>
                      <div class="fw-semibold">{{ compra.proveedor }}</div>
                      <small class="text-muted">RIF: {{ compra.rif }}</small>
                    </td>
                    <td>
                      <span class="badge bg-light text-dark">{{ compra.detalles.length }} items</span>
                    </td>
                    <td class="text-end fw-semibold">{{ formatoMoneda(compra.total) }}</td>
                    <td class="text-center">
                      <div class="btn-group btn-group-sm" role="group">
                        <button
                          class="btn btn-outline-primary"
                          @click="verDetalle(compra)"
                          data-bs-toggle="modal"
                          data-bs-target="#modalDetalleCompra"
                          title="Ver Detalle">
                          <i class="fas fa-eye"></i>
                        </button>
                        <button
                          v-if="compra.documento"
                          class="btn btn-outline-secondary"
                          @click="verDocumento(compra)"
                          title="Ver Documento">
                          <i class="fas fa-file-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Paginación -->
          <div class="card-footer" v-if="compras.length > 0">
            <div class="row align-items-center">
              <div class="col">
                <p class="mb-0 text-muted">
                  Mostrando {{ rangoInicio }} a {{ rangoFin }} de {{ compras.length }} compras
                </p>
              </div>
              <div class="col-auto">
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(paginacion.paginaActual - 1)">
                        <i class="fas fa-chevron-left"></i>
                      </a>
                    </li>
                    <li
                      class="page-item"
                      v-for="pagina in paginasVisibles"
                      :key="pagina"
                      :class="{ active: pagina === paginacion.paginaActual }"
                    >
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">
                        {{ pagina }}
                      </a>
                    </li>
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
                      <a class="page-link" href="#" @click.prevent="cambiarPagina(paginacion.paginaActual + 1)">
                        <i class="fas fa-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Detalle de Compra -->
    <div class="modal fade" id="modalDetalleCompra" tabindex="-1" aria-labelledby="modalDetalleCompraLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalDetalleCompraLabel">
              <i class="fas fa-receipt me-2"></i>
              Detalle de Compra - Comprobante {{ compraSeleccionada?.ncomprobante }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="compraSeleccionada">
            <!-- Información General -->
            <div class="row mb-4">
              <div class="col-md-6">
                <h6 class="text-muted mb-3">Información de la Compra</h6>
                <p class="mb-2"><strong>N° Comprobante:</strong> <span class="badge bg-primary">{{ compraSeleccionada.ncomprobante }}</span></p>
                <p class="mb-2"><strong>Serie:</strong> <span class="badge bg-secondary">{{ compraSeleccionada.serie }}</span></p>
                <p class="mb-2"><strong>Fecha:</strong> {{ formatearFecha(compraSeleccionada.createdAt) }} {{ formatearHora(compraSeleccionada.createdAt) }}</p>
                <p class="mb-2"><strong>Monto Total:</strong> {{ formatoMoneda(compraSeleccionada.monto_total) }}</p>
                <p class="mb-2"><strong>Monto Resultante:</strong> {{ formatoMoneda(compraSeleccionada.monto_resultante) }}</p>
                <p class="mb-2" v-if="compraSeleccionada.documento">
                  <strong>Documento:</strong>
                  <a href="#" @click.prevent="verDocumento(compraSeleccionada)" class="text-primary">
                    <i class="fas fa-file-pdf me-1"></i>Ver archivo adjunto
                  </a>
                </p>
              </div>
              <div class="col-md-6">
                <h6 class="text-muted mb-3">Información del Proveedor</h6>
                <p class="mb-2"><strong>Nombre:</strong> {{ compraSeleccionada.proveedorInfo?.nombre }}</p>
                <p class="mb-2"><strong>RIF:</strong> {{ compraSeleccionada.proveedorInfo?.tipo_rif }}-{{ compraSeleccionada.proveedorInfo?.rif }}</p>
                <p class="mb-2" v-if="compraSeleccionada.proveedorInfo?.telefono"><strong>Teléfono:</strong> {{ compraSeleccionada.proveedorInfo.telefono }}</p>
                <p class="mb-2" v-if="compraSeleccionada.proveedorInfo?.email"><strong>Email:</strong> {{ compraSeleccionada.proveedorInfo.email }}</p>
                <p class="mb-2" v-if="compraSeleccionada.proveedorInfo?.direccion"><strong>Dirección:</strong> {{ compraSeleccionada.proveedorInfo.direccion }}</p>
              </div>
            </div>

            <!-- Productos -->
            <h6 class="text-muted mb-3">Productos</h6>
            <div class="table-responsive mb-4">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Producto</th>
                    <th>SKU</th>
                    <th>Variedad</th>
                    <th class="text-center">Cantidad</th>
                    <th class="text-end">Precio Unidad</th>
                    <th class="text-end">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detalle in compraSeleccionada.detalles" :key="detalle.id">
                    <td>
                      <div class="fw-semibold">{{ detalle.producto?.titulo || `Producto #${detalle.productoId}` }}</div>
                      <small class="text-muted">{{ detalle.producto?.slug }}</small>
                    </td>
                    <td>
                      <span v-if="detalle.variedad?.sku" class="badge bg-secondary">{{ detalle.variedad.sku }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <div v-if="detalle.variedad">
                        <span v-if="detalle.variedad.talla" class="badge bg-info me-1">{{ detalle.variedad.talla }}</span>
                        <span v-if="detalle.variedad.color" class="badge bg-success me-1">{{ detalle.variedad.color }}</span>
                        <span v-if="detalle.variedad.medida" class="badge bg-warning">{{ detalle.variedad.medida }}</span>
                      </div>
                      <span v-else class="text-muted">Sin variedad</span>
                    </td>
                    <td class="text-center">{{ detalle.cantidad }}</td>
                    <td class="text-end">{{ formatoMoneda(detalle.precio_unidad) }}</td>
                    <td class="text-end fw-semibold">{{ formatoMoneda(detalle.cantidad * detalle.precio_unidad) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totales -->
            <div class="row">
              <div class="col-md-6 offset-md-6">
                <table class="table table-sm">
                  <tr class="table-primary">
                    <td class="text-end"><strong>Total:</strong></td>
                    <td class="text-end"><strong>{{ formatoMoneda(compraSeleccionada.monto_total) }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text-end"><strong>Monto Resultante:</strong></td>
                    <td class="text-end">{{ formatoMoneda(compraSeleccionada.monto_resultante) }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary m-1" data-bs-dismiss="modal">Cerrar</button>
            <button
              v-if="compraSeleccionada?.documento"
              type="button"
              class="btn btn-primary m-1"
              @click="verDocumento(compraSeleccionada)">
              <i class="fas fa-file-alt me-2"></i>
              Ver Documento
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'ConsultaCompra',
  data() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    return {
      isLoading: false,
      currentYear,
      filtros: {
        tipoPeriodo: 'dia',
        fechaEspecifica: currentDate.toISOString().split('T')[0],
        semana: '',
        mes: currentMonth.toString(),
        anioMes: currentYear,
        anio: currentYear,
        fechaInicio: '',
        fechaFin: ''
      },
      compras: [],
      compraSeleccionada: null,
      estadisticas: {
        totalIngresos: 0,
        montoInvertido: 0,
        cantidadProductos: 0
      },
      paginacion: {
        paginaActual: 1,
        porPagina: 25
      }
    };
  },
  computed: {
    comprasPaginadas() {
      const inicio = (this.paginacion.paginaActual - 1) * this.paginacion.porPagina;
      const fin = inicio + parseInt(this.paginacion.porPagina);
      return this.compras.slice(inicio, fin);
    },
    totalPaginas() {
      return Math.ceil(this.compras.length / this.paginacion.porPagina);
    },
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginacion.paginaActual;
      const paginas = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          paginas.push(i);
        }
      } else {
        if (actual <= 4) {
          for (let i = 1; i <= 5; i++) paginas.push(i);
          paginas.push('...');
          paginas.push(total);
        } else if (actual >= total - 3) {
          paginas.push(1);
          paginas.push('...');
          for (let i = total - 4; i <= total; i++) paginas.push(i);
        } else {
          paginas.push(1);
          paginas.push('...');
          for (let i = actual - 1; i <= actual + 1; i++) paginas.push(i);
          paginas.push('...');
          paginas.push(total);
        }
      }

      return paginas.filter(p => p !== '...' || paginas.indexOf(p) === paginas.lastIndexOf(p));
    },
    rangoInicio() {
      return (this.paginacion.paginaActual - 1) * this.paginacion.porPagina + 1;
    },
    rangoFin() {
      const fin = this.paginacion.paginaActual * this.paginacion.porPagina;
      return fin > this.compras.length ? this.compras.length : fin;
    }
  },
  mounted() {
    this.cargarComprasIniciales();
  },
  methods: {
    async cargarComprasIniciales() {
      // Cargar compras del día actual al iniciar
      await this.buscarCompras();
    },

    prepararParametros() {
      const params = {
        tipoPeriodo: this.filtros.tipoPeriodo
      };

      // Agregar parámetros específicos según el tipo de período
      switch (this.filtros.tipoPeriodo) {
        case 'dia':
          if (this.filtros.fechaEspecifica) {
            params.fechaEspecifica = this.filtros.fechaEspecifica;
          }
          break;

        case 'semana':
          if (this.filtros.semana) {
            params.semana = this.filtros.semana;
          }
          break;

        case 'mes':
          if (this.filtros.mes && this.filtros.anioMes) {
            params.mes = parseInt(this.filtros.mes);
            params.anioMes = parseInt(this.filtros.anioMes);
          }
          break;

        case 'anio':
          if (this.filtros.anio) {
            params.anio = parseInt(this.filtros.anio);
          }
          break;

        case 'rango':
          if (this.filtros.fechaInicio && this.filtros.fechaFin) {
            params.fechaInicio = this.filtros.fechaInicio;
            params.fechaFin = this.filtros.fechaFin;
          }
          break;
      }

      return params;
    },

    async buscarCompras() {
      this.isLoading = true;

      try {
        const params = this.prepararParametros();

        // Validar que haya al menos un parámetro de filtro
        if (Object.keys(params).length === 0) {
          this.$toast.warning('Por favor seleccione un período válido');
          this.isLoading = false;
          return;
        }

        console.log('Parámetros de búsqueda:', params);

        const response = await api.get('/ingreso/report', { params });

        console.log(response);

        // Verificar si la respuesta fue exitosa
        if (!response.data || !response.data.ok) {
          throw new Error('Error en la respuesta del servidor');
        }

        // Usar estadísticas del backend
        if (response.data.estadisticas) {
          this.estadisticas = {
            totalIngresos: response.data.estadisticas.totalIngresos || 0,
            montoInvertido: response.data.estadisticas.montoInvertido || 0,
            cantidadProductos: response.data.estadisticas.cantidadProductos || 0
          };
        }

        // Mapear la respuesta del API al formato de la tabla
        this.compras = (response.data.ingresos || []).map(ingreso => {
          // Calcular cantidad total de productos de los detalles
          const cantidadProductos = ingreso.detalles
            ? ingreso.detalles.reduce((sum, detalle) => sum + (detalle.cantidad || 0), 0)
            : 0;

          return {
            id: ingreso.id,
            ncomprobante: ingreso.ncomprobante,
            serie: ingreso.serie,
            fecha: ingreso.createdAt,
            proveedor: ingreso.proveedorInfo?.nombre || 'Sin proveedor',
            rif: ingreso.proveedorInfo?.rif || '-',
            cantidadProductos: cantidadProductos,
            total: parseFloat(ingreso.monto_total || 0),
            montoResultante: parseFloat(ingreso.monto_resultante || 0),
            documento: ingreso.documento,
            detalles: ingreso.detalles || [],
            proveedorId: ingreso.proveedorId
          };
        });

        this.paginacion.paginaActual = 1;
        this.$toast.success(`Se encontraron ${this.compras.length} compras`);
      } catch (error) {
        console.error('Error al buscar compras:', error);
        this.compras = [];
        this.estadisticas = {
          totalIngresos: 0,
          montoInvertido: 0,
          cantidadProductos: 0
        };

        let errorMessage = 'Error al cargar las compras';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$toast.error(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    limpiarFiltros() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      this.filtros = {
        tipoPeriodo: 'dia',
        fechaEspecifica: currentDate.toISOString().split('T')[0],
        semana: '',
        mes: currentMonth.toString(),
        anioMes: currentYear,
        anio: currentYear,
        fechaInicio: '',
        fechaFin: ''
      };

      this.buscarCompras();
    },

    onTipoPeriodoChange() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      // Limpiar y establecer valores por defecto según el tipo de período
      this.filtros.fechaEspecifica = '';
      this.filtros.semana = '';
      this.filtros.fechaInicio = '';
      this.filtros.fechaFin = '';

      // Establecer valor por defecto según el tipo seleccionado
      switch (this.filtros.tipoPeriodo) {
        case 'dia':
          this.filtros.fechaEspecifica = currentDate.toISOString().split('T')[0];
          break;
        case 'mes':
          this.filtros.mes = currentMonth.toString();
          this.filtros.anioMes = currentYear;
          break;
        case 'anio':
          this.filtros.anio = currentYear;
          break;
      }
    },

    cambiarPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacion.paginaActual = pagina;
      }
    },

    cambiarPorPagina() {
      this.paginacion.paginaActual = 1;
    },

    formatoMoneda(value) {
      if (!value) return '$0,00';
      const numero = Number(value).toFixed(2);
      return '$' + numero
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    formatearFecha(fecha) {
      const date = new Date(fecha);
      const dia = String(date.getDate()).padStart(2, '0');
      const mes = String(date.getMonth() + 1).padStart(2, '0');
      const anio = date.getFullYear();
      return `${dia}/${mes}/${anio}`;
    },

    formatearHora(fecha) {
      const date = new Date(fecha);
      const horas = String(date.getHours()).padStart(2, '0');
      const minutos = String(date.getMinutes()).padStart(2, '0');
      return `${horas}:${minutos}`;
    },

    async verDetalle(compra) {
      this.isLoading = true;
      try {
        const response = await api.get(`/ingreso/${compra.id}`);

        if (!response.data) {
          throw new Error('Error al obtener los detalles de la compra');
        }

        this.compraSeleccionada = response.data;
      } catch (error) {
        console.error('Error al cargar detalle de compra:', error);
        let errorMessage = 'Error al cargar los detalles de la compra';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.$toast.error(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    verDocumento(compra) {
      if (compra.documento) {
        // Construir la URL completa del documento
        const baseURL = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const documentoURL = `${baseURL}/${compra.documento}`;
        window.open(documentoURL, '_blank');
      } else {
        this.$toast.warning('No hay documento disponible');
      }
    }
  }
};
</script>

<style scoped>
.consulta-compra-container {
  padding: 0;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.stat-card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.table th {
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  text-transform: capitalize;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.pagination {
  gap: 0.25rem;
}

.page-link {
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.form-label {
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-select,
.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
}

.form-select:focus,
.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }

  .btn-group {
    flex-direction: column;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>
